// The gesture under way, whatever holds it (docs/claude/tools.md, *The gesture
// under way*): a button-held drag (main.ts DRAG_LIFE), a handle's work — the move
// gizmo's arrows and ring are handle work — and a selection grip's work, held or
// ARMED by a click (it then follows the pointer with no button held). Each kind is
// a row with the same verbs, so what must end "whatever is under way" — Esc,
// Enter, a tab switch, closing the 3D view, a lost pointer, Undo — asks this one
// life, never a mechanism by name. DOM-free: the rows are the UI's
// (ui/underWayUi.ts).

type Vec2 = { x: number; y: number };

/** What can be under way. A new kind is a member: tsc then asks for its row. */
export type UnderWayKind = 'drag' | 'grip' | 'handle';
/** The kinds a click may arm (a held drag has no armed phase). */
export type WorkKind = Exclude<UnderWayKind, 'drag'>;

/** Where a pointer event is: the plan's canvas (its screen px) or the 3D view
 *  (client px), and whether Alt is down. */
export interface UnderWayPointer { space: 'plan' | '3d'; at: Vec2; alt: boolean }

/** One kind's life, the same verbs for every kind. */
export interface UnderWayRow {
  /** Whether one is under way. */
  live(): boolean;
  /** A click armed it: it follows the pointer with no button held. */
  armed(): boolean;
  /** Where it is worked: what a press elsewhere on the page drops it for. */
  space(): 'plan' | '3d';
  /** The pointer moved: true when it is this one's (in its space). Null: its
   *  moves are not this life's (a held drag's are its DRAG_LIFE row's, which take
   *  the plan's event and its world point). */
  move: ((p: UnderWayPointer) => boolean) | null;
  /** A press while it is under way: an armed one is dropped where it is pressed
   *  (moved there, then finished). True when it took the press. */
  press(p: UnderWayPointer): boolean;
  /** Ended where it is: kept, one undo step when it changed anything. Null: it
   *  ends only on its release (a held drag: its DRAG_LIFE `end` takes the
   *  release's event). True when it ended one. */
  finish: (() => boolean) | null;
  /** Put back where its press found the drawing, nothing recorded. */
  cancel(): boolean;
  /** The drawing was replaced under it (a load): it only ends — no restore, no step. */
  drop(): void;
  /** Once a frame: ended when what owns it changed (kept) or its drawing was
   *  replaced (dropped). */
  check(): void;
  /** A figure typed on the command bar (Enter or Space): taken. Null: it takes none. */
  typed: (() => void) | null;
  /** The command bar's prompt while it is under way (null: the tool's; named
   *  `hint`: the one-dialog check, wiring.test.mjs, takes a call named for the
   *  native dialog). */
  hint(): string | null;
}

/** The order the kinds are asked in: the button-held drag first (Esc puts a pan
 *  back before the grip it pans over), then a handle's work, then a grip's. */
export const UNDER_WAY_ORDER: readonly UnderWayKind[] = ['drag', 'handle', 'grip'];

/** What Esc did to what is under way: a typed figure cleared (the caller clears
 *  it), what is under way put back, or nothing — the key goes on. */
export type EscUnderWay = 'entry' | 'cancel' | null;

/** The one life over every kind's row. */
export interface UnderWay {
  /** What is under way, the first in the order, else null. */
  now(): UnderWayKind | null;
  /** Whether what is under way holds no button (a click armed it). */
  armed(): boolean;
  /** Put back every kind under way. True when one was. */
  cancel(): boolean;
  /** Put back what holds the pointer (not armed): the pointer was lost. */
  cancelHeld(): boolean;
  /** End every kind under way that a key or a click can end, where it is. True
   *  when one was. */
  finish(): boolean;
  /** A press elsewhere on the page (not on `inside` its space): an armed one is
   *  dropped where it is, its step recorded before that press acts. */
  finishArmedOff(inside: (space: 'plan' | '3d') => boolean): void;
  /** The drawing was replaced: every kind under way only ends. */
  drop(): void;
  /** Once a frame: each kind under way ended if what owns it changed. */
  check(): void;
  /** The pointer moved: the first kind it is for takes it. */
  move(p: UnderWayPointer): boolean;
  /** A press: the first kind under way that takes it. */
  press(p: UnderWayPointer): boolean;
  /** Whether a kind under way takes typed figures (typing goes to the bar). */
  takesTyped(): boolean;
  /** A typed figure committed: the first kind under way that takes one. True
   *  when one took it. */
  typed(): boolean;
  /** The prompt of the first kind under way that has one. */
  hint(): string | null;
  /** Esc: a figure being typed for what is under way goes first (`entry`: the
   *  bar holds one) — and only that kind keeps Esc then: with a figure typed
   *  for nothing under way the key goes on —, else what is under way is put back. */
  escape(entry: boolean): EscUnderWay;
}

/** The life over `rows`, asked in `UNDER_WAY_ORDER`. */
export function underWayLife(rows: Record<UnderWayKind, UnderWayRow>): UnderWay {
  const live = (): UnderWayKind[] => UNDER_WAY_ORDER.filter((k) => rows[k].live());
  const each = (ks: UnderWayKind[], f: (r: UnderWayRow) => boolean): boolean => {
    let any = false;
    for (const k of ks) if (rows[k].live() && f(rows[k])) any = true;
    return any;
  };
  const cancel = (): boolean => each(live(), (r) => r.cancel());
  const takesTyped = (): boolean => live().some((k) => rows[k].typed != null);
  return {
    now: () => live()[0] ?? null,
    armed: () => live().some((k) => rows[k].armed()),
    cancel,
    cancelHeld: () => each(live().filter((k) => !rows[k].armed()), (r) => r.cancel()),
    finish: () => each(live(), (r) => r.finish?.() ?? false),
    finishArmedOff: (inside) => { each(live().filter((k) => rows[k].armed() && !inside(rows[k].space())), (r) => r.finish?.() ?? false); },
    drop: () => { each(live(), (r) => { r.drop(); return true; }); },
    check: () => { each(live(), (r) => { r.check(); return true; }); },
    move: (p) => live().some((k) => rows[k].move?.(p) ?? false),
    press: (p) => live().some((k) => rows[k].press(p)),
    takesTyped,
    typed: () => {
      const k = live().find((x) => rows[x].typed != null);
      if (!k) return false;
      rows[k].typed!();
      return true;
    },
    hint: () => {
      for (const k of live()) {
        const p = rows[k].hint();
        if (p != null) return p;
      }
      return null;
    },
    escape: (entry) => {
      if (!live().length) return null;
      if (entry) return takesTyped() ? 'entry' : null;
      return cancel() ? 'cancel' : null;
    },
  };
}
